/**
 * Central API client for all backend requests.
 *
 * Usage:
 *   import { api } from "@/lib/api";
 *
 *   // GET
 *   const data = await api.get<MyType>("/api/scanner/pulse");
 *   const data = await api.get<MyType>("/api/scanner/rsi-cross", { level: "30", limit: "60" });
 *
 *   // POST
 *   const data = await api.post<MyType>("/api/divergences", { min_strength: 0, limit: 200 });
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly endpoint: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface FetchOptions {
  cache?: RequestCache;
  revalidate?: number;
}

async function request<T>(
  endpoint: string,
  init: RequestInit,
  options: FetchOptions = {}
): Promise<T> {
  const fetchInit: RequestInit = { ...init };

  if (options.cache) {
    fetchInit.cache = options.cache;
  }
  if (options.revalidate !== undefined) {
    fetchInit.next = { revalidate: options.revalidate } as RequestInit["next"];
  }

  const res = await fetch(endpoint, fetchInit);

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body.error ?? body.message ?? message;
    } catch {
      // ignore JSON parse failures on error responses
    }
    throw new ApiError(message, res.status, endpoint);
  }

  return res.json() as Promise<T>;
}

export const api = {
  /**
   * GET request. Pass query params as a plain object — they are appended to the URL.
   */
  get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean>,
    options?: FetchOptions
  ): Promise<T> {
    let url = endpoint;
    if (params && Object.keys(params).length > 0) {
      const qs = new URLSearchParams(
        Object.entries(params).map(([k, v]) => [k, String(v)])
      );
      url = `${endpoint}?${qs.toString()}`;
    }
    return request<T>(url, { method: "GET" }, options);
  },

  /**
   * POST request with a JSON body.
   */
  post<T>(
    endpoint: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    return request<T>(
      endpoint,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      options
    );
  },
};
